import React from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable } from 'mobx';
import { Link, useHistory } from 'react-router-dom';
import { Alert, Badge, Button, Space, Spin, Table, Tag, Typography, notification, Popconfirm } from 'antd';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { SubmissionsDrawer } from './SubmissionsDrawer';
import { NonSubmittersDrawer } from './NonSubmittersDrawer';

const POLL_INTERVAL_MS = 15000;

function submissionCountColor(n) {
    if (n < 30) return 'red';
    if (n <= 50) return 'orange';
    return 'green';
}

const STATUS_COLORS = {
    draft: 'default',
    active: 'green',
    voting: 'blue',
    closed: 'gray',
};

const COVERAGE_WINDOW_DAYS = 14;
const MS_PER_HOUR = 3600 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Walk the scheduled-or-active challenges in chronological order and return any
 * uncovered windows in the next COVERAGE_WINDOW_DAYS. We treat each challenge as
 * occupying [startDate, startDate + duration]; gaps before the first window, between
 * consecutive challenges, and after the last one all flag the same warning so admins
 * keep a continuous schedule.
 */
function computeCoverageGaps(challenges) {
    const now = Date.now();
    const horizon = now + COVERAGE_WINDOW_DAYS * MS_PER_DAY;
    const intervals = challenges
        .filter(c => c.status !== 'closed' && c.startDate && c.duration)
        .map(c => {
            const start = new Date(c.startDate).getTime();
            return { start, end: start + c.duration * MS_PER_HOUR };
        })
        .filter(iv => iv.end > now)
        .sort((a, b) => a.start - b.start);

    const gaps = [];
    let cursor = now;
    for (const iv of intervals) {
        if (iv.start > horizon) break;
        if (iv.start > cursor) {
            gaps.push({ from: cursor, to: Math.min(iv.start, horizon) });
        }
        cursor = Math.max(cursor, iv.end);
        if (cursor >= horizon) break;
    }
    if (cursor < horizon) gaps.push({ from: cursor, to: horizon });
    return gaps;
}

function formatGap(gap) {
    const hours = Math.round((gap.to - gap.from) / MS_PER_HOUR);
    const fromLabel = new Date(gap.from).toLocaleString();
    return `${hours}h gap starting ${fromLabel}`;
}

@observer
export class Challenges extends React.Component {
    challengesProxy = new ChallengesProxy();

    @observable challenges = [];
    @observable isLoading = false;
    @observable selectedChallenge = null;
    @observable drawerOpen = false;
    @observable nonSubmittersChallenge = null;
    @observable nonSubmittersDrawerOpen = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound setIsLoading = (v) => this.isLoading = v;
    @action.bound setChallenges = (v) => this.challenges.replace(v);
    @action.bound openSubmissions = (challenge) => {
        this.selectedChallenge = challenge;
        this.drawerOpen = true;
    };
    @action.bound closeSubmissions = () => {
        this.drawerOpen = false;
    };
    @action.bound openNonSubmitters = (challenge) => {
        this.nonSubmittersChallenge = challenge;
        this.nonSubmittersDrawerOpen = true;
    };
    @action.bound closeNonSubmitters = () => {
        this.nonSubmittersDrawerOpen = false;
    };

    @computed get selectedChallengeLive() {
        if (!this.selectedChallenge) return null;
        return this.challenges.find(c => c.id === this.selectedChallenge.id) || this.selectedChallenge;
    }

    @computed get nonSubmittersChallengeLive() {
        if (!this.nonSubmittersChallenge) return null;
        return this.challenges.find(c => c.id === this.nonSubmittersChallenge.id) || this.nonSubmittersChallenge;
    }

    @computed get currentlyLiveChallengeId() {
        const now = Date.now();
        const candidates = this.challenges.filter(c => {
            if (c.status !== 'active' || !c.startDate) return false;
            const start = new Date(c.startDate).getTime();
            if (start > now) return false;
            if (c.duration && start + c.duration * MS_PER_HOUR <= now) return false;
            return true;
        });
        if (candidates.length === 0) return null;
        candidates.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
        return candidates[0].id;
    }

    @computed get coverageGaps() {
        return computeCoverageGaps(this.challenges);
    }

    componentDidMount() {
        this.fetch();
        this.pollTimer = setInterval(() => {
            if (document.visibilityState === 'visible') this.fetch({ silent: true });
        }, POLL_INTERVAL_MS);
    }

    componentWillUnmount() {
        if (this.pollTimer) clearInterval(this.pollTimer);
    }

    async fetch({ silent = false } = {}) {
        if (!silent) this.setIsLoading(true);
        try {
            const challenges = await this.challengesProxy.list();
            this.setChallenges(challenges);
        } catch (e) {
            if (!silent) notification.error({ message: 'Failed to load challenges', description: e.message });
        } finally {
            if (!silent) this.setIsLoading(false);
        }
    }

    publish = async (id) => {
        try {
            await this.challengesProxy.publish(id);
            notification.success({ message: 'Challenge published' });
            this.fetch();
        } catch (e) {
            notification.error({ message: 'Failed to publish', description: e.message });
        }
    }

    remove = async (id) => {
        try {
            await this.challengesProxy.remove(id);
            notification.success({ message: 'Challenge deleted' });
            this.fetch();
        } catch (e) {
            notification.error({ message: 'Failed to delete', description: e.message });
        }
    }

    columns = [
        {
            title: 'Image',
            dataIndex: 'image',
            width: 80,
            render: (src) => src ? <img src={src} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6 }} /> : null,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            render: (title, row) => <Link to={`/challenges/edit/${row.id}`}>{title}</Link>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 130,
            render: (status, row) => {
                const tag = <Tag color={STATUS_COLORS[status]}>{status}</Tag>;
                return row.id === this.currentlyLiveChallengeId
                    ? <Badge status="processing" text={tag} />
                    : tag;
            },
        },
        {
            title: 'Submissions',
            dataIndex: 'submissionCount',
            width: 130,
            render: (count, row) => {
                const notStarted = !row.startDate || new Date(row.startDate).getTime() > Date.now();
                if (notStarted) {
                    return <Tag style={{ fontSize: 14, padding: '2px 10px' }}>N/A</Tag>;
                }
                const n = count ?? 0;
                return (
                    <Tag
                        color={submissionCountColor(n)}
                        style={{ cursor: 'pointer', fontSize: 14, padding: '2px 10px' }}
                        onClick={() => this.openSubmissions(row)}
                    >
                        {n}
                    </Tag>
                );
            },
        },
        {
            title: 'Users entered',
            dataIndex: 'enteredUserCount',
            width: 120,
            render: (count, row) => {
                const notStarted = !row.startDate || new Date(row.startDate).getTime() > Date.now();
                if (notStarted) return <Tag style={{ fontSize: 14, padding: '2px 10px' }}>N/A</Tag>;
                return (
                    <Tag
                        style={{ cursor: 'pointer', fontSize: 14, padding: '2px 10px' }}
                        onClick={() => this.openNonSubmitters(row)}
                    >
                        {count ?? 0}
                    </Tag>
                );
            },
        },
        {
            title: 'Users rendered',
            dataIndex: 'renderedUserCount',
            width: 120,
            render: (count, row) => {
                const notStarted = !row.startDate || new Date(row.startDate).getTime() > Date.now();
                if (notStarted) return <Tag style={{ fontSize: 14, padding: '2px 10px' }}>N/A</Tag>;
                return <Tag style={{ fontSize: 14, padding: '2px 10px' }}>{count ?? 0}</Tag>;
            },
        },
        {
            title: 'Starts',
            dataIndex: 'startDate',
            width: 180,
            render: (v) => v ? new Date(v).toLocaleString() : '—',
        },
        {
            title: 'Submission close',
            dataIndex: 'submissionCloseAt',
            width: 180,
            render: (_, row) => {
                if (!row.startDate || !row.duration) return '—';
                const d = new Date(new Date(row.startDate).getTime() + row.duration * MS_PER_HOUR);
                return d.toLocaleString();
            },
        },
        {
            title: 'Actions',
            width: 240,
            render: (_, row) => (
                <Space>
                    <Link to={`/challenges/edit/${row.id}`}>
                        <Button size="small">Edit</Button>
                    </Link>
                    {(row.status === 'voting' || row.status === 'closed') && (
                        <Link to={`/challenges/results/${row.id}`}>
                            <Button size="small">Results</Button>
                        </Link>
                    )}
                    {row.status === 'draft' && (
                        <Popconfirm title="Publish this challenge?" onConfirm={() => this.publish(row.id)}>
                            <Button size="small" type="primary">Publish</Button>
                        </Popconfirm>
                    )}
                    <Popconfirm title="Delete this challenge?" onConfirm={() => this.remove(row.id)}>
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    render() {
        const gaps = this.coverageGaps;
        return (
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '24px 32px' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Typography.Title level={3} style={{ margin: 0 }}>Challenges</Typography.Title>
                    <CreateButton />
                </Space>
                {!this.isLoading && gaps.length > 0 && (
                    <Alert
                        type="error"
                        showIcon
                        message={`Coverage gap in the next ${COVERAGE_WINDOW_DAYS} days`}
                        description={
                            <div>
                                <div>Schedule a challenge to keep coverage continuous:</div>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                                    {gaps.slice(0, 3).map((g, i) => (
                                        <li key={i}>{formatGap(g)}</li>
                                    ))}
                                </ul>
                            </div>
                        }
                    />
                )}
                {this.isLoading ? <Spin /> : (
                    <Table
                        rowKey="id"
                        dataSource={this.challenges.slice()}
                        columns={this.columns}
                        pagination={{ pageSize: 20 }}
                    />
                )}
                <SubmissionsDrawer
                    key={this.selectedChallenge?.id || 'none'}
                    challenge={this.selectedChallengeLive}
                    open={this.drawerOpen}
                    onClose={this.closeSubmissions}
                />
                <NonSubmittersDrawer
                    key={`ns-${this.nonSubmittersChallenge?.id || 'none'}`}
                    challenge={this.nonSubmittersChallengeLive}
                    open={this.nonSubmittersDrawerOpen}
                    onClose={this.closeNonSubmitters}
                />
            </Space>
        );
    }
}

function CreateButton() {
    const history = useHistory();
    return <Button type="primary" onClick={() => history.push('/challenges/create')}>New challenge</Button>;
}
